'use client';
import { useState } from 'react';
import { IRepository, IResourceVersion } from '@/types';
import { useRouter } from 'next/navigation';
import { Row } from '@/ui/primatives';
import Button from '@/ui/Button';
import * as Icon from '@/ui/Icon';
import { Pencil1Icon } from '@radix-ui/react-icons';

import { CompareLinkButton } from '@/components/CompareLinkTabular';
import DownloadButton from './DownloadButton';
import useOxenSession from '@/hooks/useOxenSession';
import NewEvaluationLink from '@/components/Repository/Evaluations/NewEvaluationLink';

import { editDataframeAction } from './editDataframeAction';
import OpenMarimoSandboxButton from '@/components/Repository/Notebook/OpenMarimoSandboxButton';
import { IModalResource } from '@/lib/modalResourcesApi';

const FileDownLoadLink: React.FC<{ repository: IRepository; entry: any; fileUrl?: string }> = ({
  repository,
  entry,
  fileUrl,
}) => {
  return (
    <Row gap={10} alignItems="center">
      <Button.Basic href={fileUrl} download small style={{ padding: 10 }}>
        <Icon.Download></Icon.Download>
        {/* <span className="hide-mobile">Download</span> */}
      </Button.Basic>
      <DownloadButton repository={repository} entry={entry} fileUrl={fileUrl} />
    </Row>
  );
};

const EditDataframeButton: React.FC<{ repository: IRepository; resource: IResourceVersion }> = ({
  repository,
  resource,
}) => {
  const { authorization } = useOxenSession();
  if (!authorization.forRepository(repository).canManage()) {
    return null;
  }

  const [buttonEnabled, setButtonEnabled] = useState(true);
  const [buttonText, setButtonText] = useState('Edit');
  const [status, setStatus] = useState<string | undefined>();

  const router = useRouter();

  const onEditClicked = async () => {
    setButtonEnabled(false);
    setButtonText('Indexing...');
    const response = await editDataframeAction({ repository, resource });
    setStatus(response?.status);
    if (response?.status === 'success') {
      setButtonText('Redirecting...');
      router.push(response.redirectUrl);
    } else {
      setButtonText(`Indexing Error: ${response?.message}`);
    }
    setButtonEnabled(true);
  };

  return (
    <div className="hide-mobile">
      <Button.Basic
        className={status === 'failure' ? 'danger' : ''}
        disabled={!buttonEnabled}
        small
        onClick={onEditClicked}
        nowrap
      >
        {buttonText}
      </Button.Basic>
    </div>
  );
};

const FileControls: React.FC<{
  repository: IRepository;
  entry: any;
  resource: IResourceVersion;
  fileDownloadUrl?: string;
  isMarimoNotebook?: boolean;
  modalResources?: IModalResource[];
  isLoggedinUser?: boolean;
}> = ({ repository, entry, resource, fileDownloadUrl, isMarimoNotebook, modalResources, isLoggedinUser }) => {
  const isTabular = entry?.data_type === 'tabular';
  const isText = entry?.data_type === 'text';

  const { authorization, config } = useOxenSession();
  const noteBooksEnabled = isMarimoNotebook && modalResources && config?.notebooks_enabled;

  const fileEditEnabled = isText && authorization.forRepository(repository).canEdit();

  const fileEditUrl = `/${repository.namespace}/${repository.name}/edit/${resource.version}/${resource.path}`;

  return (
    <Row gap={10} alignItems="center">
      {isTabular && <NewEvaluationLink repository={repository} resource={resource} />}
      {isTabular && config?.remote_staging_dataframe_enabled && (
        <EditDataframeButton repository={repository} resource={resource} />
      )}
      {isTabular && (
        <CompareLinkButton entry={entry} repository={repository} version={resource.version} config={config} />
      )}
      {noteBooksEnabled && (
        <OpenMarimoSandboxButton
          repository={repository}
          entry={entry}
          modalResources={modalResources}
          isLoggedinUser={isLoggedinUser}
        />
      )}
      {fileEditEnabled && (
        <Button.Basic href={fileEditUrl} small style={{ padding: 10 }}>
          <Pencil1Icon />
        </Button.Basic>
      )}
      <FileDownLoadLink repository={repository as IRepository} entry={entry} fileUrl={fileDownloadUrl} />
    </Row>
  );
};

export default FileControls;
